// Message handling of the planner worker, kept separate so it runs in tests and as the main-thread fallback.

import type { Availability, GeoBounds, GeoCoordinate, TrailDataset, TrailRoute, WeatherForecast } from '../core/models';
import { boundsContains } from '../core/geo';
import { TrailDataStore } from '../data/store';
import { SleepTonightPlanner, type SleepTonightRequestInit, type SleepTonightResult } from '../engine';
import { HikingTimeEstimator, RouteTrack } from '../nav';
import { TileMath } from '../offline/TileMath';

export type PlannerRequest =
  | { type: 'data'; generation: number; dataset: TrailDataset }
  | {
      type: 'plan';
      id: number;
      generation: number;
      route: TrailRoute;
      personalFactor: number;
      request: SleepTonightRequestInit;
      forecast?: WeatherForecast;
      /** Bounds of completed offline downloads (Availability of each option). */
      offlineBounds: GeoBounds[];
      /** Keys "x/y" of downloaded map tiles at zoom `offlineTileZoom` (exact corridor coverage). */
      offlineTiles?: string[];
    };

export type PlannerReply =
  | { type: 'result'; id: number; result: SleepTonightResult }
  | { type: 'error'; id: number; message: string; staleData?: boolean };

let store = TrailDataStore.empty();
let storeGeneration = -1;
let cachedTrack: { routeID: string; points: number; track: RouteTrack } | undefined;

/** Zoom at which downloaded map coverage is recorded (every map package level includes it). */
export const offlineTileZoom = 14;

/**
 * iOS SleepTonightModel.availability: place data always lives on the device (bundled pack, downloads, imports), so a
 * place is at least `partiallyOffline`; `availableOffline` also needs downloaded map tiles there.
 */
function availabilityFor(bounds: readonly GeoBounds[], tiles: readonly string[] | undefined): (c: GeoCoordinate) => Availability {
  const tileSet = new Set(tiles ?? []);
  return (c) => {
    if (bounds.some((b) => boundsContains(b, c))) return 'availableOffline';
    if (tileSet.size > 0) {
      const t = TileMath.tile(c, offlineTileZoom);
      if (tileSet.has(`${t.x}/${t.y}`)) return 'availableOffline';
    }
    return 'partiallyOffline';
  };
}

export function handlePlannerMessage(message: PlannerRequest): PlannerReply | undefined {
  if (message.type === 'data') {
    store = new TrailDataStore(message.dataset);
    storeGeneration = message.generation;
    return undefined;
  }
  if (message.generation !== storeGeneration) {
    return { type: 'error', id: message.id, message: 'stale data', staleData: true };
  }
  try {
    const route = message.route;
    if (!cachedTrack || cachedTrack.routeID !== route.id || cachedTrack.points !== route.polyline.points.length) {
      cachedTrack = { routeID: route.id, points: route.polyline.points.length, track: new RouteTrack(route) };
    }
    const planner = new SleepTonightPlanner(cachedTrack.track, store, new HikingTimeEstimator(message.personalFactor));
    const result = planner.plan(message.request, message.forecast, availabilityFor(message.offlineBounds, message.offlineTiles));
    return { type: 'result', id: message.id, result };
  } catch (error) {
    return { type: 'error', id: message.id, message: error instanceof Error ? error.message : String(error) };
  }
}
